import React, { useState, useCallback, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FileText,
  Search,
  Loader,
  ChevronDown,
  ChevronUp,
  History,
  Clock,
  TrendingDown,
  MinusCircle,
  AlertCircle,
  Users,
  Calculator,
  Download,
  Eye,
  RefreshCw,
  X,
  Calendar
} from 'lucide-react';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { AuthContext } from '../../context/AuthContext';

const DeductionReport = ({
  staff,
  deductionData: propDeductionData,
  loading,
  fetchDeductionData,
  setError,
  setSuccess
}) => {
  const { logout } = useContext(AuthContext);
  const navigate = useNavigate();

  const getAuthToken = () => {
    return localStorage.getItem('adminAuthToken') || localStorage.getItem('kioskAuthToken') || localStorage.getItem('authToken');
  };

  // State management - simplified to match backend
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [availablePeriods, setAvailablePeriods] = useState([]);
  const [reportData, setReportData] = useState([]);
  const [selectedStaffFilter, setSelectedStaffFilter] = useState('');
  const [customReportLoading, setCustomReportLoading] = useState(false);
  const [expandedStaff, setExpandedStaff] = useState({});

  // Get current month in YYYY-MM format
  const getCurrentPeriod = () => {
    const now = new Date();
    const lagosNow = new Date(now.toLocaleString("en-US", { timeZone: "Africa/Lagos" }));
    return `${lagosNow.getFullYear()}-${String(lagosNow.getMonth() + 1).padStart(2, '0')}`;
  };

  // Load available periods from backend
  const loadAvailablePeriods = useCallback(async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/payroll/available-periods`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAvailablePeriods(data.periods || []);
        
        // Auto-select current period if available
        const currentPeriod = getCurrentPeriod();
        if (data.periods.includes(currentPeriod)) {
          setSelectedPeriod(currentPeriod);
        } else if (data.periods.length > 0) {
          // Select most recent period available
          setSelectedPeriod(data.periods[0]);
        }
        
        console.log('Available periods loaded:', data.periods);
      }
    } catch (err) {
      console.error('Error loading available periods:', err);
      setError('Failed to load available periods');
    }
  }, [setError]);

  // Load deduction report - bow to backend completely
  const loadDeductionReport = useCallback(async () => {
    if (!selectedPeriod || customReportLoading) return;
    
    try {
      setCustomReportLoading(true);
      
      // SIMPLE: Just send period to backend, let it handle everything
      const queryParams = {
        period: selectedPeriod
      };

      if (selectedStaffFilter) {
        queryParams.employeeId = selectedStaffFilter;
      }

      console.log('Loading deduction report for period:', selectedPeriod);

      const response = await fetchDeductionData(queryParams);

      if (response && response.deductionsData) {
        setReportData(response.deductionsData);
        console.log(`Loaded ${response.deductionsData.length} deduction records for ${selectedPeriod}`);
      } else {
        setReportData([]);
        console.log(`No deduction data found for ${selectedPeriod}`);
      }

    } catch (err) {
      console.error('Error loading deduction report:', err);
      if (err.message && err.message.includes('404')) {
        setError(`No data available for ${selectedPeriod}. Try running historical recalculation.`);
      } else {
        setError('Failed to load deduction report');
      }
      setReportData([]);
    } finally {
      setCustomReportLoading(false);
    }
  }, [selectedPeriod, selectedStaffFilter, fetchDeductionData, setError]);

  // Load available periods on mount
  useEffect(() => {
    loadAvailablePeriods();
  }, [loadAvailablePeriods]);

  // Load data when period changes
  useEffect(() => {
    if (selectedPeriod) {
      loadDeductionReport();
    }
  }, [selectedPeriod, selectedStaffFilter, loadDeductionReport]);

  // Toggle staff expansion
  const toggleStaffExpansion = (staffId) => {
    setExpandedStaff(prev => ({
      ...prev,
      [staffId]: !prev[staffId]
    }));
  };

  // Format currency
  const formatCurrency = (amount) => `₦${Math.round(amount || 0).toLocaleString('en-NG')}`;

  // Format period for display
  const formatPeriodDisplay = (period) => {
    if (!period) return '';
    const [year, month] = period.split('-');
    const date = new Date(year, month - 1);
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  // Currency formatting function for PDF exports
  const formatCurrencyForPDF = (amount) => {
    if (!amount || isNaN(amount)) return 'N0';
    return 'N' + Math.round(amount).toLocaleString('en-US');
  };

  // FIXED: PDF Export with all corrections
  const exportDeductionReport = useCallback((format) => {
    if (!reportData || reportData.length === 0) {
      setError('No deduction data to export');
      setTimeout(() => setError(''), 3000);
      return;
    }

    const reportTitle = `Deduction Report - ${formatPeriodDisplay(selectedPeriod)} (${selectedPeriod})`;

    if (format === 'pdf') {
      try {
        const doc = new jsPDF();
        let yPosition = 20;

        // Title and header
        doc.setFontSize(18);
        doc.setFont('helvetica', 'bold');
        doc.text(reportTitle, 14, yPosition);
        yPosition += 10;
        
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.text(`Generated: ${new Date().toLocaleDateString()}`, 14, yPosition);
        doc.text(`System: Shift-Based Dynamic Rates`, 120, yPosition);
        yPosition += 15;

        // Summary statistics
        const totalDeductions = reportData.reduce((sum, record) => sum + record.totalDeduction, 0);
        const totalInstances = reportData.reduce((sum, record) => sum + (record.deductions?.length || 0), 0);
        const affectedStaff = new Set(reportData.map(r => r.employeeId)).size;
        const avgPerStaff = totalDeductions / Math.max(1, affectedStaff);

        const summaryData = [
          ['Metric', 'Value'],
          ['Total Deductions', formatCurrencyForPDF(totalDeductions)],
          ['Deduction Instances', totalInstances.toString()],
          ['Staff Affected', affectedStaff.toString()],
          ['Average per Staff', formatCurrencyForPDF(avgPerStaff)]
        ];

        autoTable(doc, {
          startY: yPosition,
          head: [summaryData[0]],
          body: summaryData.slice(1),
          theme: 'grid',
          headStyles: { fillColor: [220, 38, 127], textColor: 255, fontStyle: 'bold' },
          styles: { fontSize: 9, cellPadding: 3 },
          columnStyles: {
            0: { cellWidth: 60, fontStyle: 'bold' },
            1: { cellWidth: 40, halign: 'right', fontStyle: 'bold' }
          }
        });

        yPosition = doc.lastAutoTable.finalY + 15;

        // Staff overview table
        const staffGrouped = reportData.reduce((acc, record) => {
          const key = record.employeeId;
          if (!acc[key]) {
            acc[key] = {
              employeeInfo: {
                employeeId: record.employeeId,
                employeeName: record.employeeName,
                position: record.position,
                department: record.department,
                baseSalary: record.baseSalary
              },
              records: [],
              totalDeduction: 0,
              days: 0
            };
          }
          acc[key].records.push(record);
          acc[key].totalDeduction += record.totalDeduction;
          acc[key].days += 1;
          return acc;
        }, {});

        const staffOverviewData = Object.values(staffGrouped)
          .sort((a, b) => b.totalDeduction - a.totalDeduction)
          .map(staff => [
            `${staff.employeeInfo.employeeName}\n(${staff.employeeInfo.employeeId})`,
            staff.employeeInfo.position,
            staff.employeeInfo.department,
            formatCurrencyForPDF(staff.employeeInfo.baseSalary),
            staff.days.toString(),
            formatCurrencyForPDF(staff.totalDeduction)
          ]);

        autoTable(doc, {
          startY: yPosition,
          head: [['Employee', 'Position', 'Department', 'Base Salary', 'Days', 'Total Deducted']],
          body: staffOverviewData,
          theme: 'striped',
          headStyles: { fillColor: [34, 197, 94], textColor: 255, fontStyle: 'bold', fontSize: 8 },
          styles: { fontSize: 7, cellPadding: 2 },
          columnStyles: {
            0: { cellWidth: 40, fontStyle: 'bold' },
            1: { cellWidth: 25 },
            2: { cellWidth: 25 },
            3: { cellWidth: 25, halign: 'right' },
            4: { cellWidth: 15, halign: 'center' },
            5: { cellWidth: 25, halign: 'right', fontStyle: 'bold', fillColor: [254, 226, 226] }
          }
        });

        // Individual staff breakdowns (new pages for each staff)
        Object.values(staffGrouped)
          .sort((a, b) => b.totalDeduction - a.totalDeduction)
          .forEach((staffData) => {
            // Add new page for each staff member
            doc.addPage();
            yPosition = 20;

            // Staff header
            doc.setFontSize(14);
            doc.setFont('helvetica', 'bold');
            doc.text(
              `${staffData.employeeInfo.employeeName} (${staffData.employeeInfo.employeeId}) - ${formatCurrencyForPDF(staffData.totalDeduction)}`,
              14, yPosition
            );
            yPosition += 15;

            // Staff details
            doc.setFontSize(9);
            doc.setFont('helvetica', 'normal');
            doc.text(`Position: ${staffData.employeeInfo.position}`, 14, yPosition);
            doc.text(`Department: ${staffData.employeeInfo.department}`, 80, yPosition);
            doc.text(`Base Salary: ${formatCurrencyForPDF(staffData.employeeInfo.baseSalary)}`, 140, yPosition);
            yPosition += 15;

            // FIXED: Deduction details table with cleaned descriptions
            const deductionDetails = [];
            
            staffData.records
              .sort((a, b) => b.date.localeCompare(a.date))
              .forEach(record => {
                const dateObj = new Date(record.date + 'T12:00:00');
                const formattedDate = dateObj.toLocaleDateString('en-US', { 
                  year: 'numeric', 
                  month: 'short', 
                  day: 'numeric'
                });

                if (record.deductions && record.deductions.length > 0) {
                  record.deductions.forEach(deduction => {
                    const duration = deduction.minutes ? `${deduction.minutes} min` : '-';
                    
                    // FIXED: Clean calculation description (no grace info)
                    let calculationDesc = '';
                    if (deduction.calculationMethod === 'shift_based') {
                      if (deduction.type !== 'Absence') {
                        calculationDesc = deduction.perMinuteRate 
                          ? `${formatCurrencyForPDF(deduction.perMinuteRate)}/min`
                          : 'Per-minute';
                      } else {
                        // FIXED: Proper formatting for absence with currency symbol
                        calculationDesc = deduction.isHalfDayAbsence 
                          ? `Half-day (${formatCurrencyForPDF(deduction.fullDayRate || 0)}/2)`
                          : `Full-day (${formatCurrencyForPDF(deduction.fullDayRate || 0)})`;
                      }
                    } else {
                      calculationDesc = deduction.calculationMethod || '-';
                    }
                    
                    deductionDetails.push([
                      formattedDate,
                      deduction.type || 'Unknown',
                      formatCurrencyForPDF(deduction.amount),
                      duration,
                      calculationDesc,
                      '' // REMOVED: No description column with grace info
                    ]);
                  });
                }
              });

            if (deductionDetails.length > 0) {
              autoTable(doc, {
                startY: yPosition,
                head: [['Date', 'Type', 'Amount', 'Duration', 'Calculation']], // REMOVED: Description column
                body: deductionDetails,
                theme: 'grid',
                headStyles: { 
                  fillColor: [59, 130, 246], 
                  textColor: 255, 
                  fontStyle: 'bold',
                  fontSize: 8
                },
                styles: { fontSize: 7, cellPadding: 3 },
                columnStyles: {
                  0: { cellWidth: 30, fontStyle: 'bold' },
                  1: { cellWidth: 30, fontStyle: 'bold' },
                  2: { cellWidth: 28, halign: 'right', fontStyle: 'bold' },
                  3: { cellWidth: 22, halign: 'center' },
                  4: { cellWidth: 50, fontSize: 7 } // WIDER: More space for calculation
                },
                alternateRowStyles: { fillColor: [248, 250, 252] }
              });
            }

            // FIXED: Staff totals summary with Net Pay instead of Average per Day
            yPosition = doc.lastAutoTable.finalY + 10;
            
            const staffTotalDeductions = staffData.totalDeduction;
            const staffTotalDays = staffData.days;
            const staffNetPay = staffData.employeeInfo.baseSalary - staffTotalDeductions; // FIXED: Net Pay

            doc.setFontSize(10);
            doc.setFont('helvetica', 'bold');
            doc.text(`Summary for ${staffData.employeeInfo.employeeName}:`, 14, yPosition);
            yPosition += 8;
            
            doc.setFontSize(9);
            doc.setFont('helvetica', 'normal');
            doc.text(`Total Days with Deductions: ${staffTotalDays}`, 14, yPosition);
            doc.text(`Total Amount Deducted: ${formatCurrencyForPDF(staffTotalDeductions)}`, 80, yPosition);
            doc.text(`Net Pay: ${formatCurrencyForPDF(staffNetPay)}`, 140, yPosition); // FIXED: Net Pay
          });

        // FIXED: Footer on all pages with correct branding
        const pageCount = doc.internal.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
          doc.setPage(i);
          doc.setFontSize(8);
          doc.setFont('helvetica', 'normal');
          doc.text(`Ella'sPlace Salon, Spa & Gym - Shift-Based Deduction Report`, 14, 285); // FIXED: Footer
          doc.text(`Page ${i} of ${pageCount}`, 170, 285);
        }

        doc.save(`${reportTitle}.pdf`);
        setSuccess('Detailed shift-based deduction report exported as PDF successfully');
      } catch (error) {
        console.error('PDF generation error:', error);
        setError('Failed to generate PDF. Please try again.');
      }
    } else if (format === 'excel') {
      // Excel export (unchanged)
      try {
        const workbook = XLSX.utils.book_new();
        
        // Summary Sheet
        const totalDeductions = reportData.reduce((sum, record) => sum + record.totalDeduction, 0);
        const totalInstances = reportData.reduce((sum, record) => sum + (record.deductions?.length || 0), 0);
        const affectedStaff = new Set(reportData.map(r => r.employeeId)).size;

        const summaryData = [
          [reportTitle],
          [`Generated: ${new Date().toLocaleDateString()}`],
          [`Period: ${selectedPeriod}`],
          [`System: Shift-Based Dynamic Rates`],
          [`Staff Filter: ${selectedStaffFilter || 'All'}`],
          [],
          ['SUMMARY STATISTICS'],
          ['Total Deductions', totalDeductions],
          ['Deduction Instances', totalInstances],
          ['Staff Affected', affectedStaff],
          ['Average per Staff', Math.round(totalDeductions / Math.max(1, affectedStaff))]
        ];

        const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
        XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');

        // Detailed breakdown sheet with calculation details
        const detailHeaders = [
          'Date', 
          'Employee Name', 
          'Employee ID', 
          'Position', 
          'Department', 
          'Base Salary', 
          'Deduction Type', 
          'Amount', 
          'Duration (min)', 
          'Calculation Method',
          'Per-Minute Rate',
          'Full-Day Rate',
          'Shift Hours'
        ];
        const detailData = [detailHeaders];

        reportData.forEach(record => {
          if (record.deductions && record.deductions.length > 0) {
            record.deductions.forEach(deduction => {
              detailData.push([
                record.date,
                record.employeeName,
                record.employeeId,
                record.position,
                record.department,
                record.baseSalary,
                deduction.type,
                deduction.amount,
                deduction.minutes || '',
                deduction.calculationMethod || '',
                deduction.perMinuteRate || '',
                deduction.fullDayRate || '',
                deduction.workingHoursUsed || ''
              ]);
            });
          }
        });

        const detailSheet = XLSX.utils.aoa_to_sheet(detailData);
        XLSX.utils.book_append_sheet(workbook, detailSheet, 'Detailed Breakdown');

        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        const data = new Blob([excelBuffer], { type: 'application/octet-stream' });
        saveAs(data, `${reportTitle}.xlsx`);
        setSuccess('Shift-based deduction report exported as Excel successfully');
      } catch (error) {
        console.error('Excel generation error:', error);
        setError('Failed to generate Excel file. Please try again.');
      }
    }

    setTimeout(() => setSuccess(''), 3000);
  }, [reportData, selectedPeriod, selectedStaffFilter, formatPeriodDisplay, setSuccess, setError]);
        
  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h2 className="text-2xl font-semibold mb-6 text-gray-900 flex items-center gap-2">
          <FileText className="text-blue-600" size={24} />
          Comprehensive Deduction Report
        </h2>

        {/* Simplified Filters - Bow to Backend */}
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <Search className="text-blue-600" size={20} />
            Report Filters
          </h3>
          
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {/* Period Selection - Backend Dictates */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <History className="inline mr-1" size={16} />
                Available Periods
              </label>
              <select
                value={selectedPeriod}
                onChange={(e) => setSelectedPeriod(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-sm"
                disabled={availablePeriods.length === 0}
              >
                <option value="">Select period...</option>
                {availablePeriods.map(period => (
                  <option key={period} value={period}>
                    {formatPeriodDisplay(period)} ({period})
                  </option>
                ))}
              </select>
              {availablePeriods.length === 0 && (
                <p className="text-xs text-red-500 mt-1">No periods available. Run historical recalculation.</p>
              )}
            </div>

            {/* Staff Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Users className="inline mr-1" size={16} />
                Filter by Staff
              </label>
              <select
                value={selectedStaffFilter}
                onChange={(e) => setSelectedStaffFilter(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-sm"
              >
                <option value="">All Staff</option>
                {staff.map(staffMember => (
                  <option key={staffMember._id} value={staffMember.employeeId}>
                    {staffMember.firstName} {staffMember.lastName} ({staffMember.employeeId})
                  </option>
                ))}
              </select>
            </div>

            {/* Refresh Button */}
            <div className="flex items-end">
              <button
                onClick={() => {
                  loadAvailablePeriods();
                  if (selectedPeriod) loadDeductionReport();
                }}
                disabled={customReportLoading}
                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 px-6 rounded-lg hover:from-blue-700 hover:to-purple-700 disabled:from-gray-400 disabled:to-gray-500 transition-all duration-200 flex items-center justify-center gap-2 font-medium shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                {customReportLoading ? (
                  <>
                    <Loader className="animate-spin" size={18} />
                    Loading...
                  </>
                ) : (
                  <>
                    <RefreshCw size={18} />
                    Load Report
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Period Info */}
          {selectedPeriod && (
            <div className="mt-4 p-3 bg-white rounded-lg border border-blue-200">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Selected Period:</span>
                <span className="font-semibold text-blue-700">
                  {formatPeriodDisplay(selectedPeriod)} ({selectedPeriod})
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Loading State */}
        {customReportLoading && (
          <div className="bg-blue-50 rounded-lg p-6">
            <div className="flex items-center justify-center gap-3 p-4 py-12">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              <span className="text-gray-600">Loading deduction report for {selectedPeriod}...</span>
            </div>
          </div>
        )}

        {/* No Period Selected */}
        {!selectedPeriod && !customReportLoading && (
          <div className="text-center py-16 bg-gray-50 rounded-lg">
            <FileText className="mx-auto h-16 w-16 text-gray-300 mb-4" />
            <h3 className="text-xl font-medium text-gray-600 mb-2">Select Period for Report</h3>
            <p className="text-gray-500 mb-4">Choose from available periods to generate deduction report</p>
          </div>
        )}

        {/* No Data Found */}
        {!customReportLoading && selectedPeriod && reportData.length === 0 && (
          <div className="text-center py-16 bg-gray-50 rounded-lg">
            <FileText className="mx-auto h-16 w-16 text-gray-300 mb-4" />
            <h3 className="text-xl font-medium text-gray-600 mb-2">No Deductions Found</h3>
            <p className="text-gray-500 mb-4">No deduction records found for {formatPeriodDisplay(selectedPeriod)}</p>
            <p className="text-gray-400">This period may have no deduction data</p>
          </div>
        )}

        {/* Staff-Grouped Report Display */}
        {!customReportLoading && reportData.length > 0 && (
          <div className="space-y-4">
            {/* Summary Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-6">
              <div className="bg-gradient-to-r from-red-50 to-red-100 p-4 rounded-lg border border-red-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-red-800">Total Deductions</p>
                    <p className="text-2xl font-bold text-red-600">
                      {formatCurrency(
                        reportData.reduce((sum, record) => sum + record.totalDeduction, 0)
                      )}
                    </p>
                  </div>
                  <MinusCircle className="text-red-500" size={32} />
                </div>
              </div>
              <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-4 rounded-lg border border-blue-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-blue-800">Deduction Days</p>
                    <p className="text-2xl font-bold text-blue-600">{reportData.length}</p>
                  </div>
                  <Calendar className="text-blue-500" size={32} />
                </div>
              </div>
              <div className="bg-gradient-to-r from-green-50 to-green-100 p-4 rounded-lg border border-green-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-green-800">Staff Affected</p>
                    <p className="text-2xl font-bold text-green-600">
                      {new Set(reportData.map(r => r.employeeId)).size}
                    </p>
                  </div>
                  <Users className="text-green-500" size={32} />
                </div>
              </div>
              <div className="bg-gradient-to-r from-purple-50 to-purple-100 p-4 rounded-lg border border-purple-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-purple-800">Avg Per Staff</p>
                    <p className="text-2xl font-bold text-purple-600">
                      {formatCurrency(
                        reportData.reduce((sum, record) => sum + record.totalDeduction, 0) / 
                        Math.max(1, new Set(reportData.map(r => r.employeeId)).size)
                      )}
                    </p>
                  </div>
                  <Calculator className="text-purple-500" size={32} />
                </div>
              </div>
            </div>

            {/* Export Options */}
            <div className="mb-6 p-4 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg border border-green-200">
              <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <Download className="text-green-600" size={20} />
                Export Report
              </h3>
              <div className="flex flex-wrap gap-3">
                <button 
                  onClick={() => exportDeductionReport('pdf')}
                  className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors shadow-md hover:shadow-lg"
                >
                  <FileText size={16} />
                  Export PDF
                </button>
                <button 
                  onClick={() => exportDeductionReport('excel')}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors shadow-md hover:shadow-lg"
                >
                  <Download size={16} />
                  Export Excel
                </button>
                <button 
                  onClick={() => window.print()}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-md hover:shadow-lg"
                >
                  <Eye size={16} />
                  Print Preview
                </button>
              </div>
            </div>

            {/* Staff-Grouped Breakdown */}
            <div className="space-y-4">
              {Object.entries(
                reportData.reduce((acc, record) => {
                  const key = record.employeeId;
                  if (!acc[key]) {
                    acc[key] = {
                      employeeInfo: {
                        employeeId: record.employeeId,
                        employeeName: record.employeeName,
                        position: record.position,
                        department: record.department,
                        baseSalary: record.baseSalary
                      },
                      records: [],
                      totalDeduction: 0,
                      deductionCount: 0
                    };
                  }
                  acc[key].records.push(record);
                  acc[key].totalDeduction += record.totalDeduction;
                  acc[key].deductionCount += record.deductions?.length || 0;
                  return acc;
                }, {})
              )
              .sort(([,a], [,b]) => b.totalDeduction - a.totalDeduction)
              .map(([employeeId, staffData]) => {
                const isExpanded = expandedStaff[employeeId];
                const { employeeInfo, records, totalDeduction, deductionCount } = staffData;

                return (
                  <div key={employeeId} className="border border-gray-200 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                    <button
                      onClick={() => toggleStaffExpansion(employeeId)}
                      className="w-full flex justify-between items-center p-6 bg-gradient-to-r from-gray-50 to-gray-100 hover:from-gray-100 hover:to-gray-200 transition-all duration-200"
                    >
                      <div className="flex items-center gap-6">
                        <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                          {employeeInfo.employeeName.charAt(0)}
                        </div>
                        <div className="text-left">
                          <h3 className="text-xl font-bold text-gray-900">{employeeInfo.employeeName}</h3>
                          <div className="flex items-center gap-3 text-sm text-gray-600">
                            <span className="bg-blue-100 px-2 py-1 rounded-full text-blue-700 font-medium">
                              {employeeInfo.employeeId}
                            </span>
                            <span>{employeeInfo.position}</span>
                            <span>•</span>
                            <span>{employeeInfo.department}</span>
                          </div>
                          <p className="text-sm text-green-600 font-semibold mt-1">
                            Base Salary: {formatCurrency(employeeInfo.baseSalary)}
                          </p>
                        </div>
                        <div className="flex gap-3">
                          <div className="bg-white px-4 py-2 rounded-full border shadow-sm">
                            <span className="text-sm font-semibold text-gray-700">
                              {records.length} day{records.length !== 1 ? 's' : ''}
                            </span>
                          </div>
                          <div className="bg-red-100 px-4 py-2 rounded-full border border-red-200">
                            <div className="flex items-center gap-2">
                              <AlertCircle size={16} className="text-red-600" />
                              <span className="text-sm font-semibold text-red-700">
                                {deductionCount} incident{deductionCount !== 1 ? 's' : ''}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="bg-red-100 px-4 py-2 rounded-lg border border-red-200">
                          <p className="text-xs text-red-600 font-medium mb-1">Total Deducted</p>
                          <p className="text-2xl font-bold text-red-700">{formatCurrency(totalDeduction)}</p>
                        </div>
                        <div className="bg-white p-2 rounded-full shadow-sm">
                          {isExpanded ? 
                            <ChevronUp size={24} className="text-gray-600" /> : 
                            <ChevronDown size={24} className="text-gray-600" />
                          }
                        </div>
                      </div>
                    </button>

                    {isExpanded && (
                      <div className="bg-white p-6 space-y-4">
                        {records
                          .sort((a, b) => b.date.localeCompare(a.date))
                          .map((record, index) => {
                            const dateObj = new Date(record.date + 'T12:00:00');
                            const formattedDate = dateObj.toLocaleDateString('en-US', { 
                              year: 'numeric', 
                              month: 'long', 
                              day: 'numeric',
                              timeZone: 'Africa/Lagos'
                            });
                            const dayName = dateObj.toLocaleDateString('en-US', { 
                              weekday: 'long', 
                              timeZone: 'Africa/Lagos' 
                            });

                            return (
                              <div key={index} className="border border-gray-100 rounded-lg p-4 hover:bg-gray-50 transition-colors">
                                <div className="flex justify-between items-start mb-3">
                                  <div>
                                    <h4 className="text-lg font-semibold text-gray-900">
                                      {formattedDate}
                                    </h4>
                                    <p className="text-sm text-gray-600">{dayName}</p>
                                  </div>
                                  <div className="bg-red-100 px-3 py-1 rounded-lg border border-red-200">
                                    <span className="text-sm font-bold text-red-700">
                                      {formatCurrency(record.totalDeduction)}
                                    </span>
                                  </div>
                                </div>

                                {record.deductions && record.deductions.length > 0 && (
                                  <div className="space-y-2">
                                    <h5 className="text-sm font-medium text-gray-700">Deduction Details:</h5>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                      {record.deductions.map((deduction, i) => {
                                        const getDeductionColor = (type) => {
                                          switch (type.toLowerCase()) {
                                            case 'lateness': return 'bg-orange-50 border-orange-200 text-orange-800';
                                            case 'early leave': return 'bg-yellow-50 border-yellow-200 text-yellow-800';
                                            case 'absence': return 'bg-red-50 border-red-200 text-red-800';
                                            default: return 'bg-gray-50 border-gray-200 text-gray-800';
                                          }
                                        };

                                        const getDeductionIcon = (type) => {
                                          switch (type.toLowerCase()) {
                                            case 'lateness': return <Clock size={16} />;
                                            case 'early leave': return <TrendingDown size={16} />;
                                            case 'absence': return <MinusCircle size={16} />;
                                            default: return <AlertCircle size={16} />;
                                          }
                                        };

                                        return (
                                          <div key={i} className={`p-3 rounded-lg border-2 ${getDeductionColor(deduction.type)}`}>
                                            <div className="flex items-center justify-between mb-2">
                                              <div className="flex items-center gap-2">
                                                {getDeductionIcon(deduction.type)}
                                                <span className="text-sm font-bold">{deduction.type}</span>
                                              </div>
                                              <span className="text-lg font-bold">{formatCurrency(deduction.amount)}</span>
                                            </div>
                                            {deduction.minutes && (
                                              <p className="text-xs font-medium opacity-75 mb-1">
                                                Duration: {deduction.minutes} minutes
                                              </p>
                                            )}
                                            {deduction.calculationMethod && (
                                              <p className="text-xs text-blue-600 mt-1 font-medium">
                                                {deduction.calculationMethod}
                                              </p>
                                            )}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DeductionReport;